<script setup lang="ts">
/**
 * One execution on the `/executions` board: what the old table row said, stacked into a card.
 * The status pill stays because a lane is coarser than a status — Blocked holds waiting work and
 * anything with a run on Blockers, Complete holds failed and stopped work someone has dealt with
 * as well as the successes — and BG §22.3 says the word carries the meaning.
 *
 * Drawn on the kit Card, with the kit Button as its open affordance, so the card states no surface,
 * hover treatment or type size of its own.
 */
import { ArrowRight } from '@lucide/vue'
import { Button, Card, CardContent, ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { type ExecutionSummary, formatAgo, formatDuration } from '@/api'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { EXECUTION_PAUSED_HOLD, executionStoppedNote } from '@/lib/executionStop'
import { executionPillStatus } from '@/lib/statusTone'

const props = defineProps<{ execution: ExecutionSummary; now: number }>()

/** Which task stopped this execution and why, when a task ended it itself (#810); the detail page says the same. */
const stoppedNote = computed(() => executionStoppedNote(props.execution))

const duration = computed(() =>
    formatDuration(
        Math.max(
            0,
            (props.execution.finishedAt ? new Date(props.execution.finishedAt).getTime() : props.now) -
                new Date(props.execution.startedAt).getTime(),
        ),
    ),
)
const startedAgo = computed(() => formatAgo(Math.max(0, props.now - new Date(props.execution.startedAt).getTime())))
/** Who paused this execution and how long ago, while it is paused (#809); the detail page says the same. */
const pausedNote = computed(() => {
    const { status, pausedBy, pausedAt } = props.execution
    if (status !== 'paused' || !pausedAt) return null
    const ago = formatAgo(Math.max(0, props.now - new Date(pausedAt).getTime()))
    return `Paused${pausedBy ? ` by ${pausedBy.name}` : ''} · ${ago}`
})
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`
</script>

<template>
    <Card :data-execution-id="execution.id" class="text-sm">
        <CardContent>
            <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                    <RouterLink :to="`/executions/${execution.id}`" class="hover:text-primary block font-medium">{{ execution.name }}</RouterLink>
                    <span class="text-muted-foreground mt-0.5 block font-mono text-xs">{{ execution.id.slice(0, 8) }} <span v-if="execution.lineage === 'standalone'" class="font-sans">· single run</span></span>
                </div>
                <!-- A link, drawn as the kit icon Button: the RouterLink is the element, the Button its look. -->
                <Button as-child variant="ghost" size="icon-xs">
                    <RouterLink :to="`/executions/${execution.id}`" :aria-label="`Open ${execution.name}`"><ArrowRight :size="14" /></RouterLink>
                </Button>
            </div>
            <div class="mt-2">
                <ExecutionStatus :status="executionPillStatus(execution)" />
                <p v-if="pausedNote" class="text-muted-foreground mt-1.5 text-xs" data-testid="execution-paused-note">{{ pausedNote }}<span class="block" data-testid="execution-paused-hold">{{ EXECUTION_PAUSED_HOLD }}</span></p>
                <p v-if="stoppedNote" class="text-muted-foreground mt-1.5 text-xs" data-testid="execution-stopped-note">{{ stoppedNote }}</p>
                <p v-if="execution.currentTasks.length" class="text-muted-foreground mt-1.5 text-xs">{{ execution.currentTasks.map(task => task.name).join(' · ') }}</p>
                <!-- The work items this execution holds or worked (#1830): the card is theirs too, and opens each. -->
                <p v-if="execution.workItems?.length" class="text-muted-foreground mt-1.5 text-xs" data-testid="execution-work-items">
                    <template v-for="(item, index) in execution.workItems" :key="item.id"><span v-if="index > 0"> · </span><RouterLink :to="`/work-items/${item.id}`" class="underline underline-offset-2">{{ item.title }}</RouterLink></template>
                    <span class="ml-1">· work item{{ execution.workItems.length === 1 ? '' : 's' }}</span>
                </p>
            </div>
            <div class="mt-3 flex items-center gap-2">
                <div v-if="execution.members.length" class="flex gap-1"><MemberAvatar v-for="member in execution.members.slice(0, 3)" :key="member.id" :member="member" class="size-6" fallback-class="text-xs" /></div>
                <span class="text-xs">{{ execution.members.map(member => member.name).join(', ') || 'OS' }}</span>
            </div>
            <p class="text-muted-foreground mt-1 text-xs">{{ execution.teams.map(team => team.name).join(' · ') || 'Company-wide' }}</p>
            <p class="text-muted-foreground mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums">
                <ZhTooltip :text="new Date(execution.startedAt).toLocaleString()"><span class="whitespace-nowrap">Started {{ startedAgo }}</span></ZhTooltip>
                <span class="whitespace-nowrap">{{ duration }}</span>
                <span class="whitespace-nowrap">{{ plural(execution.runCount, 'run') }} · {{ plural(execution.taskCount, 'task') }}</span>
            </p>
        </CardContent>
    </Card>
</template>
